// src/layouts/AppShell.tsx
import * as React from "react";
import { Outlet, Link, useLocation } from "react-router-dom";

import GlobalAppearanceToggle from "@/components/chrome/GlobalAppearanceToggle";
import AuthoritySurfaceSwitcher from "@/components/nav/AuthoritySurfaceSwitcher";
import TopModulesNav from "@/components/nav/TopModulesNav";
import UiToaster from "@/components/ui/UiToaster";

import { useActivaClass } from "@/hooks/useActivaClass";
import { ActivaProvider, useActiva } from "@/context/ActivaContext";
import { AppAppearanceProvider } from "@/context/AppAppearanceContext";

import AppNavigatorBinder from "@/AppNavigatorBinder";
import NavAuthCta from "@/components/nav/NavAuthCta";
import { AppInteriorFailClosedGate } from "@/features/auth";

import { installOpsStateBus, installOpsDebugHook } from "@/contracts/ops/OpsStateBus";
import { installWiring } from "@/wiring/installWiring";
import { scheduleOmniCinePrefetch } from "@/lib/routePrefetch";

import { DiagnosticsProvider } from "@/context/DiagnosticsContext";
import { resolveRouteShellPolicy, shouldForceDesktopShell, type RouteShellPolicy } from "@/config/shellPolicy";

const LazySettingsDrawerRoute = React.lazy(
  () => import("@/components/settings/SettingsDrawerRoute")
);

/* ------------------------------------------------------------------ */
/* Mobile heuristic (v1)                                               */
/* ------------------------------------------------------------------ */

function isProbablyMobile(): boolean {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth || 1024;

  // Touch hints (not perfect, but fine for shell branching)
  const touch =
    "ontouchstart" in window ||
    (navigator as any).maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  // Keep OmniCine “desktop-first”: treat narrow screens as mobile
  return w < 860 || touch;
}

/* ------------------------------------------------------------------ */
/* Shared installers (minimal)                                         */
/* ------------------------------------------------------------------ */

function useGlobalInstallers(opts: { enableWiring: boolean; enableOpsBus: boolean }) {
  // Ops StateBus (optional)
  React.useEffect(() => {
    if (!opts.enableOpsBus) return;

    const uninstallBus = installOpsStateBus();
    const uninstallDbg = import.meta.env.DEV ? installOpsDebugHook() : () => {};

    return () => {
      try {
        uninstallDbg();
        uninstallBus();
      } catch {
        // never throw from shell cleanup
      }
    };
  }, [opts.enableOpsBus]);

  // Canonical wiring installer (capabilities/manifests) — desktop-only by default
  React.useEffect(() => {
    if (!opts.enableWiring) return;

    const uninstall = installWiring();
    return () => {
      try {
        uninstall();
      } catch {}
    };
  }, [opts.enableWiring]);
}

function shouldShowFloatingAppearanceToggle(pathname: string): boolean {
  return (
    !pathname.startsWith("/guided-studio") &&
    !pathname.startsWith("/omnistudio") &&
    !pathname.startsWith("/omnicine") &&
    !pathname.startsWith("/command-deck")
  );
}

/* ------------------------------------------------------------------ */
/* Desktop shell                                                       */
/* ------------------------------------------------------------------ */

function DesktopShellChrome({
  policy,
  shouldRenderSettingsDrawer,
}: {
  policy: RouteShellPolicy;
  shouldRenderSettingsDrawer: boolean;
}): JSX.Element {
  const { state, enableMock } = useActiva();
  const activaCls = useActivaClass(state) || "";
  const showFloatingAppearanceToggle = shouldShowFloatingAppearanceToggle(policy.pathname);

  // Allow mock Activa during local dev to animate chrome
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    enableMock(true);
    return () => {
      try {
        enableMock(false);
      } catch {}
    };
  }, [enableMock]);

  // Desktop gets wiring + ops bus
  useGlobalInstallers({ enableWiring: true, enableOpsBus: true });

  // Sync activa class onto <html data-activa="...">
  React.useEffect(() => {
    document.documentElement.setAttribute("data-activa", activaCls);
  }, [activaCls]);

  React.useEffect(() => {
    scheduleOmniCinePrefetch();
  }, []);

  const cssVars = React.useMemo<React.CSSProperties>(
    () => ({
      ["--app-header-h" as any]: policy.headerMode === "hidden" ? "0px" : "64px",
      ["--rail-left-nudge" as any]: "-14px",
      ["--rail-top-nudge" as any]: "8px",
    }),
    [policy.headerMode],
  );

  return (
    <div
      className="min-h-screen bg-white text-slate-900 dark:bg-black dark:text-white"
      style={cssVars}
    >
      {policy.headerMode === "hidden" ? null : (
        <header className="sticky top-0 z-50 backdrop-blur-md bg-white/60 dark:bg-black/30 border-b border-black/10 dark:border-white/10">
          <div className="mx-auto max-w-[1400px] px-4 py-2 flex items-center gap-4">
            <Link to="/" aria-label="SynkTribe home" className="shrink-0 inline-flex items-center">
              <span
                className="text-lg font-semibold tracking-wide bg-clip-text text-transparent
                         bg-[linear-gradient(90deg,#7dd3fc,#a78bfa,#34d399)]
                         bg-[length:200%_100%]
                         motion-safe:animate-[shine_8s_linear_infinite]"
              >
                SYNKTRIBE
              </span>
            </Link>

            <nav className="flex-1 flex items-center justify-center">
              <TopModulesNav />
            </nav>

            <div
              className="hidden sm:flex items-center gap-2 pointer-events-auto"
              aria-label="Account and subscription"
            >
              <NavAuthCta />
            </div>
          </div>
        </header>
      )}

      {policy.showAuthoritySwitcher ? <AuthoritySurfaceSwitcher /> : null}

      {showFloatingAppearanceToggle ? <GlobalAppearanceToggle /> : null}

      <div className={policy.headerMode === "hidden" ? "hidden" : "sticky top-[var(--app-header-h,64px)] z-30"}>
        {/* ticker mount */}
      </div>

      <main
        id="app-main"
        className={`${policy.headerMode === "hidden" ? "min-h-screen" : "min-h-[calc(100vh-4rem)]"} focus:outline-none activa-surface ${activaCls}`}
        data-activa={state}
      >
        <AppNavigatorBinder />
        <AppInteriorFailClosedGate>
          <Outlet />
        </AppInteriorFailClosedGate>
      </main>

      <React.Suspense fallback={null}>
        {shouldRenderSettingsDrawer ? <LazySettingsDrawerRoute /> : null}
      </React.Suspense>

      {/* global toaster (bus-driven) */}
      

      <style>{`
        @keyframes shine {
          0% { background-position: 0% 50% }
          100% { background-position: 200% 50% }
        }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mobile shell (lighter)                                              */
/* ------------------------------------------------------------------ */

function MobileShellChrome({
  policy,
  shouldRenderSettingsDrawer,
}: {
  policy: RouteShellPolicy;
  shouldRenderSettingsDrawer: boolean;
}): JSX.Element {
  const { state, enableMock } = useActiva();
  const activaCls = useActivaClass(state) || "";
  const showFloatingAppearanceToggle = shouldShowFloatingAppearanceToggle(policy.pathname);

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    enableMock(true);
    return () => {
      try {
        enableMock(false);
      } catch {}
    };
  }, [enableMock]);

  // Mobile: default to NO wiring (heavy) + optional ops bus off
  // If you want ops events on mobile, flip enableOpsBus to true.
  useGlobalInstallers({ enableWiring: false, enableOpsBus: false });

  React.useEffect(() => {
    document.documentElement.setAttribute("data-activa", activaCls);
  }, [activaCls]);

  React.useEffect(() => {
    scheduleOmniCinePrefetch();
  }, []);

  // Mobile vars: no rail nudges; shorter header
  const cssVars = React.useMemo<React.CSSProperties>(
    () => ({
      ["--app-header-h" as any]: policy.headerMode === "hidden" ? "0px" : "52px",
      ["--rail-left-nudge" as any]: "0px",
      ["--rail-top-nudge" as any]: "0px",
    }),
    [policy.headerMode],
  );

  return (
    <div
      className="min-h-screen bg-white text-slate-900 dark:bg-black dark:text-white"
      style={cssVars}
    >
      {policy.headerMode === "hidden" ? null : (
        <header className="sticky top-0 z-50 backdrop-blur-md bg-white/70 dark:bg-black/35 border-b border-black/10 dark:border-white/10">
          <div className="mx-auto max-w-[1400px] px-3 py-2">
            <div className="flex items-center gap-3">
              <Link to="/" aria-label="SynkTribe home" className="shrink-0 inline-flex items-center">
                <span className="text-base font-semibold tracking-wide">SYNKTRIBE</span>
              </Link>

              <div className="min-w-0 flex-1 flex items-center justify-center">
                <TopModulesNav compact />
              </div>

              <div className="shrink-0 flex items-center gap-2">
                <NavAuthCta />
              </div>
            </div>
          </div>
        </header>
      )}

      {policy.showAuthoritySwitcher ? <AuthoritySurfaceSwitcher /> : null}

      {showFloatingAppearanceToggle ? <GlobalAppearanceToggle /> : null}

      <main
        id="app-main"
        className={`${policy.headerMode === "hidden" ? "min-h-screen" : "min-h-[calc(100vh-var(--app-header-h,52px))]"} focus:outline-none activa-surface ${activaCls}`}
        data-activa={state}
      >
        <AppNavigatorBinder />
        <AppInteriorFailClosedGate>
          <Outlet />
        </AppInteriorFailClosedGate>
      </main>

      <React.Suspense fallback={null}>
        {shouldRenderSettingsDrawer ? <LazySettingsDrawerRoute /> : null}
      </React.Suspense>

      {/* global toaster (bus-driven) */}
      <UiToaster />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shell router                                                        */
/* ------------------------------------------------------------------ */

function ShellRouter(): JSX.Element {
  const loc = useLocation();
  const policy = React.useMemo(() => resolveRouteShellPolicy(loc.pathname), [loc.pathname]);
  const shouldRenderSettingsDrawer = React.useMemo(() => {
    const params = new URLSearchParams(loc.search);
    const panel = (params.get("panel") || "").toLowerCase();
    return (
      params.get("settings") === "1" ||
      panel === "settings" ||
      panel === "devices"
    );
  }, [loc.search]);

  const forceDesktop = shouldForceDesktopShell(loc.pathname);

  const mobile = !forceDesktop && isProbablyMobile();
  return mobile ? (
    <MobileShellChrome
      policy={policy}
      shouldRenderSettingsDrawer={shouldRenderSettingsDrawer}
    />
  ) : (
    <DesktopShellChrome
      policy={policy}
      shouldRenderSettingsDrawer={shouldRenderSettingsDrawer}
    />
  );
}

/* ------------------------------------------------------------------ */
/* AppShell                                                            */
/* ------------------------------------------------------------------ */

export default function AppShell(): JSX.Element {
  return (
    <AppAppearanceProvider>
      <ActivaProvider>
        <DiagnosticsProvider>
          <ShellRouter />
        </DiagnosticsProvider>
      </ActivaProvider>
    </AppAppearanceProvider>
  );
}
